/**
 * Engines: the reading apps built by hand.
 *
 * Some things a two-sentence prompt cannot write well: continuous blending
 * with a finger as the playhead, a ladder of phonics levels, spaced review
 * of sight words. Those are engines. A material that uses one stores only a
 * config (which engine, which words, which character slides under the
 * letters), and the host expands it into the app at launch. The generator
 * writes the config; the app itself ships with the client, so every engine
 * material improves when the app does.
 */

import { blendedWords, blendLevelFromMemory, decodableLevel, GRAPHEMES, LEVELS, SIGHT_WORDS, SPOKEN_WORDS, WORD_BY_TEXT, wordFamilies } from './phonics'
import { holds, knowsOnSight, type ItemMemory } from '../memory'
import { bankPicture, GENERATORS, validateDeck, type CardFace, type Deck } from './decks'

export interface EngineMaterial {
  engine: string
  config: Record<string, unknown>
}

interface EngineSpec {
  /** For the generator: what it is, when to pick it, and its config. */
  brief: string
  /** Every setting it reads: anything else in a config would be ignored without a word, so it is refused. */
  keys: string[]
  validate: (config: Record<string, unknown>) => string[]
}

/** The setting shared by the engines that draw words from the word bank: words never to show her (a pig, when pigs scare her). */
const SKIP = 'skip?: [word-bank words never to show her, in words, pictures or choices, e.g. something she is scared of]'
const skipError = (c: Record<string, unknown>) => wordsError(c.skip, 'skip', (w) => !!WORD_BY_TEXT[w], 'outside the word bank')

const MARK = /<script[^>]*type=["']application\/sapling-engine\+json["'][^>]*>([\s\S]*?)<\/script>/i

/** The stored form of an engine material. */
export function engineMaterialHtml(engine: string, config: Record<string, unknown>): string {
  const json = JSON.stringify({ engine, config }).replace(/</g, '\\u003c')
  return `<script type="application/sapling-engine+json">${json}</script>`
}

/** The engine and config a stored material names, or null for a hand-written one. */
export function readEngineMaterial(html: string): EngineMaterial | null {
  const m = MARK.exec(html ?? '')
  if (!m) return null
  try {
    const v = JSON.parse(m[1]!)
    if (!v || typeof v.engine !== 'string') return null
    return { engine: v.engine, config: v.config && typeof v.config === 'object' ? v.config : {} }
  } catch {
    return null
  }
}

const intIn = (v: unknown, lo: number, hi: number) => v === undefined || (Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi)
const shortText = (v: unknown, max: number) => v === undefined || (typeof v === 'string' && v.length <= max)

function wordsError(v: unknown, field: string, test: (w: string) => boolean, what: string): string[] {
  if (v === undefined) return []
  if (!Array.isArray(v) || v.some((w) => typeof w !== 'string')) return [`${field} must be a list of words`]
  const bad = (v as string[]).filter((w) => !test(w.toLowerCase()))
  return bad.length ? [`${field} has words ${what}: ${bad.slice(0, 8).join(', ')}`] : []
}

/** Word-bank pictures of how someone is (a crying blob for sad), which a scene would draw as a second creature. */
const NOT_THINGS = new Set(['sad', 'red'])

/** Things drawn in the sky: a page can be "in the sun" without anything sitting in it. */
const SKY = new Set(['sun', 'moon', 'star', 'rain', 'storm'])

/**
 * A page whose words put one scene thing on or in another must draw it so
 * (rel, with the thing it sits on or in second): a picture that shows them
 * side by side teaches her to guess against the text. A question answered no
 * names a place someone is not, so it asks nothing of the picture.
 */
export function relationProblems(p: { text: string; scene: unknown[]; rel?: unknown }, i: number): string[] {
  if (p.rel !== undefined && p.rel !== 'on' && p.rel !== 'in') return [`page ${i + 1}: rel is on or in`]
  const scene = p.scene.map((x) => String(x).toLowerCase())
  if (p.rel !== undefined && scene.length < 2) return [`page ${i + 1}: rel needs two things in the scene, the one on top or inside first`]
  if (scene.length < 2 || /\?\s*no\b/i.test(p.text)) return []
  for (const m of p.text.toLowerCase().matchAll(/\b(on|in)\s+(?:the\s+|a\s+|an\s+|his\s+|her\s+)?([a-z']+)/g)) {
    const under = m[2]!.replace(/'s$/, '')
    if (SKY.has(under) || !scene.includes(under)) continue
    if (p.rel === undefined || scene[1] !== under) return [`page ${i + 1} says "${m[0]}": put the ${under} second in its scene, after what is ${m[1]} it, and set rel to "${m[1]}"`]
  }
  return []
}

export const ENGINES: Record<string, EngineSpec> = {
  blend: {
    brief: `"blend": sound-by-sound blending, the core of learning to read. She slides a character under a word; each sound plays as it passes, held sounds stretch while her finger rests, and at the end the sounds join into the word and its picture appears. Some rounds she picks the picture that matches the word she blended. It climbs a five-step ladder on its own (${LEVELS.map((l) => `${l.n} ${l.name.toLowerCase()}`).join(', ')}), moving up when she is sailing and back when she is stuck, and remembers where she got to between sessions. Pick it whenever the goal is decoding, blending, phonics, or letter sounds.
Config: { level?: 1-5 where to start the first time (later sessions start where she left off), words?: [words from the word bank to focus on, all one level or mixed], guide?: one emoji that slides under the letters (her current interest: a dinosaur, a rocket, a ladybug), rounds?: 5-12 (default 8), ${SKIP} }.
Word bank by level: ${LEVELS.map((l) => `${l.n}: ${Object.values(WORD_BY_TEXT).filter((w) => w.level === l.n).map((w) => w.word).join(' ')}`).join(' | ')}.`,
    keys: ['level', 'words', 'guide', 'rounds', 'skip'],
    validate: (c) => [
      ...(intIn(c.level, 1, 5) ? [] : ['level must be a whole number from 1 to 5']),
      ...(intIn(c.rounds, 5, 12) ? [] : ['rounds must be a whole number from 5 to 12']),
      ...skipError(c),
      ...(shortText(c.guide, 16) ? [] : ['guide must be a single emoji']),
      ...wordsError(c.words, 'words', (w) => !!WORD_BY_TEXT[w], 'outside the word bank (each word needs a picture and a recorded voice)'),
    ],
  },
  sight: {
    brief: `"sight": the words that cannot be sounded out (said, they, the, was), learned by seeing them whole. A new word is met first, alone and big, said out loud; then the voice says a word and she finds it among a few that look alike (the, they, then), so she looks at the whole word. Words she finds fly into a row of caught words. It spaces review over sessions, bringing a word back less often as she keeps getting it. Pick it for sight words, tricky words, high-frequency words, or a word hunt.
It also plays letters instead of words: the voice says a sound (mmm, never a letter name) and she finds its letter, and a letter she has met comes as three pictures where she finds the one that starts with that sound. Pick that for letter sounds, first sounds, or hearing the sound a word starts with.
Config: { words: [the sight words to work on today, 1 to 4 of them], rounds?: 6-14 (default 10) } or, for letters, { letters: [2 to 6 single letters, each a different sound], rounds?, ${SKIP} }.
Sight words it knows: ${SIGHT_WORDS.join(' ')}.`,
    keys: ['words', 'letters', 'rounds', 'skip'],
    validate: (c) => c.letters !== undefined
      ? [
          ...skipError(c),
          ...(Array.isArray(c.letters) && c.letters.length >= 2 && c.letters.length <= 6 && c.letters.every((l) => typeof l === 'string' && /^[a-z]$/.test(l) && GRAPHEMES[l]) ? [] : ['letters must list 2 to 6 single lowercase letters']),
          ...(Array.isArray(c.letters) && new Set(c.letters.map((l) => GRAPHEMES[String(l)])).size === c.letters.length ? [] : ['letters must each make a different sound']),
          ...(c.words === undefined ? [] : ['give words or letters, not both']),
          ...(intIn(c.rounds, 6, 14) ? [] : ['rounds must be a whole number from 6 to 14']),
        ]
      : [
          ...(Array.isArray(c.words) && c.words.length >= 1 && c.words.length <= 4 ? [] : ['words must list 1 to 4 sight words']),
          ...(intIn(c.rounds, 6, 14) ? [] : ['rounds must be a whole number from 6 to 14']),
          ...wordsError(c.words, 'words', (w) => SIGHT_WORDS.some((s) => s.toLowerCase() === w), 'outside the sight word list (each needs a recorded voice)'),
        ],
  },
  families: {
    brief: `"families": word families, reading by chunks. A family's ending (at, op, ing) sits on screen with a gap in front; she picks a first letter, it flies in, the word sounds itself out and its picture joins the family, until she hears them all rhyme; then she finds one of them by its first sound. Pick it for word families, rhyming, onset and rime, "change the first letter", or once blending single words is going well.
Config: { families?: [2 to 4 family endings to work on today, in order], level?: 1 to 5, ${SKIP} } (families left out, it picks the ones she has met least, filled only with words at her blending level, or at \`level\` when it is set: at level 1 that is words with a first sound she can stretch, like man, fan, van).
Families it knows: ${Object.entries(wordFamilies()).map(([k, f]) => `${k} (${f.words.map((w) => w.word).join(' ')})`).join('; ')}.`,
    keys: ['families', 'level', 'skip'],
    validate: (c) => {
      const known = wordFamilies()
      if (skipError(c).length) return skipError(c)
      if (c.level !== undefined && !intIn(c.level, 1, 5)) return ['level must be a whole number from 1 to 5']
      if (c.families === undefined) return []
      if (!Array.isArray(c.families) || c.families.length < 1 || c.families.length > 4) return ['families must list 1 to 4 family endings']
      const bad = (c.families as unknown[]).map((f) => String(f).replace(/^[-_ ]+/, '').toLowerCase()).filter((f) => !known[f])
      return bad.length ? [`families it cannot fill: ${bad.join(', ')}; it knows ${Object.keys(known).join(', ')}`] : []
    },
  },
  story: {
    brief: `"story": a little book she reads herself, the core of fluency. A cover, then 3 to 8 pages, each a picture scene and one short sentence in big type. She reads it herself: nothing reads the page to her. Tapping a word says it when the voice has it recorded (the word bank and the words below) and sounds it out otherwise, so lean on those words; holding a word sounds it out. The scenes are the word bank's own clay pictures, so the text describes them as they look. A listener she likes sits on each page and reacts as she reads to it. Her reading time is kept between sessions and the end shows this read beside the last, so the same book re-read is a game (repeated reading is how fluency comes). Pick it for reading practice, fluency, decodable books, "read to an audience", or re-reading. A book read aloud to her with the words lighting up, or one with pictures made for its own characters, is HTML.
Every word must be readable by her: one of the accepted words below, or a word she can sound out at the given level (words with a silent e, "ea", "y" as a vowel and the like are refused), or a name listed in names. Keep it to about 95% easy: short sentences of words she knows, repeated patterns, a tiny story with a problem and a happy end, themed to what she loves.
Config: { title: string, level: 1-5 (her blending level; every word is checked against it), names?: [proper names used, e.g. her own], listener?: one emoji (her teddy, a cat), cover?: a word-bank word or an emoji, pages: [{ text: one or two short sentences, scene: [1 to 4 things: the main character first, then who or what is with it, then one thing in the sky if any. Each is a word from the word bank, which is drawn as a clay figure (prefer these), or an emoji], place?: grass | mud | sand | snow | sea | night | home | space (sets the sky and ground; do not use shape emoji to paint a setting), rel?: on | in (when the line puts one thing on or in another, the first scene thing sits on or in the second, so the picture shows what the words say) }] }.
Words beyond the word bank it accepts at any level (each has a recorded voice): ${SPOKEN_WORDS.join(' ')}.`,
    keys: ['title', 'level', 'names', 'listener', 'cover', 'pages'],
    validate: (c) => {
      const errors: string[] = []
      if (typeof c.title !== 'string' || !c.title.trim() || c.title.length > 40) errors.push('title must be a short string')
      if (!intIn(c.level, 1, 5) || c.level === undefined) errors.push('level must be a whole number from 1 to 5')
      if (!shortText(c.listener, 16) || !shortText(c.cover, 16)) errors.push('listener and cover must be single emoji')
      const names = new Set((Array.isArray(c.names) ? c.names : []).map((n) => String(n).toLowerCase()))
      const pages = Array.isArray(c.pages) ? c.pages : []
      if (pages.length < 3 || pages.length > 8) errors.push('pages must have 3 to 8 pages')
      const texts: string[] = [typeof c.title === 'string' ? c.title : '']
      pages.forEach((p: any, i) => {
        if (!p || typeof p.text !== 'string' || !p.text.trim()) return errors.push(`page ${i + 1} needs text`)
        if (p.text.split(/\s+/).length > 14) errors.push(`page ${i + 1} is too long; keep each page to one or two short sentences`)
        if (!Array.isArray(p.scene) || p.scene.length < 1 || p.scene.length > 4) errors.push(`page ${i + 1} needs a scene of 1 to 4 things`)
        else errors.push(...relationProblems(p, i))
        // A word for how someone is draws as a creature of its own beside them.
        const states = Array.isArray(p.scene) ? p.scene.filter((x: unknown) => typeof x === 'string' && NOT_THINGS.has(x)) : []
        if (states.length) errors.push(`page ${i + 1}: ${states.join(', ')} ${states.length > 1 ? 'are' : 'is'} how someone is, not a thing to draw; leave it to the words`)
        texts.push(p.text)
      })
      const level = Number(c.level) || 5
      const sight = new Set(SPOKEN_WORDS.map((w) => w.toLowerCase()))
      const hard = new Set<string>()
      for (const t of texts) {
        for (const raw of t.split(/[^A-Za-z']+/)) {
          const w = raw.toLowerCase().replace(/'s$/, '')
          if (!w || sight.has(w) || names.has(w)) continue
          const need = decodableLevel(w)
          if (need === null || need > level) hard.add(need === null ? w : `${w} (level ${need})`)
        }
      }
      if (hard.size) errors.push(`these words are not readable at level ${level}; use words she can sound out, sight words, or add a name to names: ${[...hard].slice(0, 12).join(', ')}`)
      // A word she meets as a word is still that word with a capital: Cat as
      // a name is the level 2 word cat, so it cannot carry a harder word into the book.
      const posing = (Array.isArray(c.names) ? c.names : []).map(String).filter((n) => !!WORD_BY_TEXT[n.toLowerCase()] && (decodableLevel(n.toLowerCase()) ?? 0) > level)
      if (posing.length) errors.push(`${posing.join(', ')} ${posing.length > 1 ? 'are words' : 'is a word'} above level ${level}, not a name; pick a name she can sound out at level ${level}`)
      return errors
    },
  },
  cards: {
    brief: `"cards": flash cards the way a father taught his two-year-old to read with Anki: a few decks, every deck every day. One session is one stack of every deck's cards, no stops between decks; her memory picks each deck's cards (due first, a few new ones when she is doing well, and nothing she already knows that is not due, so a day is as short as it can be), a missed card goes to the bottom of the stack once, and the end shows her decks and a row of days, no numbers. Card kinds: read (she reads it aloud, a parent holds Got it or Not yet, then the card turns over to its picture and the voice says it), choose (she picks the back, or the front itself, among options; checks itself), listen (the voice says the front; she finds it among options), generator (made fresh each time: pattern, add, subtract, count, after, before, rhyme). Usually the app builds this from the family's decks; configure it yourself for a small one-off deck, or for plain practice of what its generators make (patterns, sums, counting, what comes after) when that is what was asked.
Config: { decks: [Deck: { id: short slug, title, direction: the family's sentence, cover?: word-bank word or emoji, daily?: 3-20 (default 10), maxNew?: new cards a day, cards: [{ id: slug, kind: read | choose | listen | generator, front: { text?, picture?: a word-bank word or an asset id, emoji?, say?: a voice key (w:cat, p:sh) or plain text }, back?: a face, options?: [the wrong answers, as faces], gen?: { type, params?: { max?, rule?: AB | AAB | ABB | ABC | AABB, set?: colors | shapes | animals | letters | both } } }] }], session?: { size?: cards per deck, order?: given | shuffle } }.`,
    keys: ['decks', 'session'],
    validate: (c) => {
      const decks = Array.isArray(c.decks) ? (c.decks as Deck[]) : null
      if (!decks || decks.length < 1 || decks.length > 12) return ['decks must list 1 to 12 decks']
      const errors = decks.flatMap((d, i) => [...validateDeck(d), ...playProblems(d)].map((e) => `deck ${(d && typeof d.title === 'string' && d.title) || i + 1}: ${e}`))
      const ids = decks.map((d) => d?.id)
      if (new Set(ids).size !== ids.length) errors.push('two decks share an id')
      const s = (c.session ?? {}) as Record<string, unknown>
      if (typeof s !== 'object' || !intIn(s.size, 3, 20)) errors.push('session.size must be a whole number from 3 to 20')
      if (s.order !== undefined && s.order !== 'given' && s.order !== 'shuffle') errors.push('session.order must be given or shuffle')
      return errors
    },
  },
}

// Every engine refuses a setting it does not read: the change asked for
// would otherwise be saved and do nothing, and the parent be told it was made.
for (const spec of Object.values(ENGINES)) {
  const own = spec.validate
  spec.validate = (c) => {
    const unknown = Object.keys(c ?? {}).filter((k) => !spec.keys.includes(k))
    return [...(unknown.length ? [`no setting called ${unknown.join(', ')}; its settings are ${spec.keys.join(', ')}`] : []), ...own(c)]
  }
}

// One emoji (a flag, a keycap, a family joined with zero-width joiners), nothing that could be markup.
const SYMBOL = /^(?:[^\x00-\x7f]|[0-9#*]){1,8}$/u
const PICTOGRAPH = /[\p{Extended_Pictographic}\p{Regional_Indicator}\u20e3]/u
function symbol(s: string): boolean {
  return SYMBOL.test(s) && PICTOGRAPH.test(s) && [...s].length <= 8
}

/**
 * What validateDeck lets through but the cards player cannot play: a
 * generator it does not know (the deck would say its title and end), and a
 * cover or emoji that is not one: the player shows these, and a deck is
 * data, never code.
 */
function playProblems(d: Deck): string[] {
  if (!d || typeof d !== 'object' || !Array.isArray(d.cards)) return []
  const errors: string[] = []
  if (d.cover !== undefined && !(typeof d.cover === 'string' && (bankPicture(d.cover) || symbol(d.cover) || /^[\p{L}\p{N}]{1,4}$/u.test(d.cover)))) {
    errors.push('cover must be a word-bank word, one emoji, or up to four letters')
  }
  d.cards.forEach((c, i) => {
    if (!c || typeof c !== 'object') return
    const at = `card ${i + 1}`
    if (c.kind === 'generator' && c.gen?.type && !Object.keys(GENERATORS).includes(c.gen.type)) {
      errors.push(`${at}: there is no generator "${c.gen.type}"; they are ${Object.keys(GENERATORS).join(', ')}`)
    }
    const faces = [c.front, c.back, ...(Array.isArray(c.options) ? c.options : [])] as (CardFace | undefined)[]
    if (faces.some((f) => f && f.emoji !== undefined && !(typeof f.emoji === 'string' && symbol(f.emoji)))) errors.push(`${at}: emoji must be a single emoji`)
  })
  return errors
}

/**
 * Days in a row the cards were done, from what the cards engine carries
 * (carry.days: local YYYY-MM-DD dates). A streak still counts on a day not
 * yet played, so the morning does not read as broken.
 */
export function cardsStreak(carry: unknown, now = new Date()): number {
  const days = new Set(Array.isArray((carry as any)?.days) ? ((carry as any).days as unknown[]).filter((d): d is string => typeof d === 'string') : [])
  const key = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const d = new Date(now)
  if (!days.has(key(d))) d.setDate(d.getDate() - 1)
  let n = 0
  while (days.has(key(d))) { n++; d.setDate(d.getDate() - 1) }
  return n
}

/** Check an engine material's config. Empty when it is good to save. */
export function validateEngineMaterial(m: EngineMaterial): string[] {
  const spec = ENGINES[m.engine]
  if (!spec) return [`there is no engine called "${m.engine}"; the engines are ${Object.keys(ENGINES).join(', ')}`]
  return spec.validate(m.config)
}

/** For the generator's brief: every engine, what it is for, and its config. */
export function enginesBrief(): string {
  return Object.values(ENGINES).map((e) => `- ${e.brief}`).join('\n')
}



/**
 * Where she has got to in an engine material, in a few words for the parent:
 * "Level 3 of 5 · 24 words read". From her item memory where the engine
 * records to it, else from the material's carry. Null before the first play,
 * or for a material that keeps no memory.
 */
export function engineProgress(html: string, carry: unknown, memory?: Record<string, ItemMemory>): string | null {
  const m = readEngineMaterial(html)
  const c = carry && typeof carry === 'object' ? (carry as Record<string, any>) : {}
  if (!m) return null
  const count = (o: unknown) => (o && typeof o === 'object' ? Object.keys(o).length : 0)
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  switch (m.engine) {
    case 'blend': {
      // From her memory when there is one: the level she holds and every word
      // she has sounded out anywhere, the numbers the reading path names too.
      const read = memory ? blendedWords(memory).length : 0
      if (read) {
        const held = LEVELS.find((l) => l.n === blendLevelFromMemory(memory!, Date.now()))
        return `${held ? `Level ${held.n} of ${LEVELS.length}, ${held.name.toLowerCase()}` : 'No level yet'} · ${plural(read, 'word', 'words')} read`
      }
      const level = LEVELS.find((l) => l.n === c.level)
      return level ? `Level ${level.n} of ${LEVELS.length}, ${level.name.toLowerCase()} · ${plural(count(c.seen), 'word', 'words')} read` : null
    }
    case 'sight': {
      if (Array.isArray(m.config.letters)) {
        const now = Date.now()
        const letters = (m.config.letters as unknown[]).map(String)
        const known = letters.filter((l) => holds(memory?.[`sound:${GRAPHEMES[l]}`], now))
        return known.length ? `Knows the sounds of ${known.join(' ')}` : null
      }
      // Her memory is the truth; the boxes in carry are what older sessions kept.
      const now = Date.now()
      const words = Object.values(memory ?? {}).filter((x) => x.item.startsWith('sight:') && x.reps > 0)
      const boxes: number[] = c.box && typeof c.box === 'object' ? Object.values(c.box) : []
      const [met, sure] = words.length
        ? [words.length, words.filter((x) => knowsOnSight(x, now)).length]
        : [boxes.length, boxes.filter((b) => b >= 3).length]
      return met ? `${plural(met, 'word', 'words')} met · ${sure} she knows on sight` : null
    }
    case 'story': {
      // Re-reads are told by ease (how much she read on her own), never speed.
      const reads = Array.isArray(c.times) ? c.times.filter((t: unknown) => typeof t === 'number' && t > 0).length : 0
      if (!reads) return null
      const helps: number[] = Array.isArray(c.helps) ? c.helps.filter((n: unknown) => typeof n === 'number' && n >= 0) : []
      const last = helps[helps.length - 1]
      const ease = last === undefined ? '' : last === 0 ? (reads > 1 ? 'the last on her own' : 'on her own') : `${plural(last, 'word', 'words')} of help ${reads > 1 ? 'the last time' : ''}`.trim()
      return [`Read ${plural(reads, 'time', 'times')}`, ease].filter(Boolean).join(' · ')
    }
    case 'families': {
      const n = count(c.met)
      return n ? `${plural(n, 'family', 'families')} met` : null
    }
    case 'cards': {
      const now = Date.now()
      const decks = new Set((Array.isArray(m.config.decks) ? m.config.decks : []).map((d: any) => `card:${String(d?.id).toLowerCase()}/`))
      const known = Object.values(memory ?? {}).filter((x) => decks.has(x.item.slice(0, x.item.indexOf('/') + 1)) && holds(x, now)).length
      const streak = cardsStreak(c)
      if (!known && !streak) return null
      return [known ? `${plural(known, 'card', 'cards')} known` : '', streak > 1 ? `${streak} days in a row` : ''].filter(Boolean).join(' · ') || null
    }
  }
  return null
}
